import type { RouteConfig } from 'vue-router';

import Vue from 'vue';

import { useAccountsStore } from '@/store/accounts';
import { useSidepanelStore } from '@/store/side-panel';
import { useProfileData } from '@/store/user-data';

import SiteFooter from '@/components/common/SiteFooter.vue';
import BaseAppBar from '@/components/widgets/app-bar/BaseAppBar.vue';

const CabinetLayout = () => import('@/layouts/CabinetLayout.vue');
const LoginLayout = () => import('@/features(FSD)/login-layout').then((m) => m.LoginLayout);

const RoleBasedCabinetSidebar = () => import('@/components/cabinet/RoleBasedCabinetSidebar.vue');

const routes: RouteConfig[] = [
  {
    name: 'index',
    path: '/',
    redirect: { name: 'board' },
  },
  {
    name: 'account',
    path: '/account',
    components: {
      default: LoginLayout,
      bar: BaseAppBar,
    },
    children: [
      {
        name: 'login',
        path: 'login',
        meta: {
          pageTitle: 'Авторизация',
        },
        component: () => import('@/pages/login').then((m) => m.Login),
      },
      {
        name: 'createAccount',
        path: 'create',
        meta: {
          pageTitle: 'Регистрация',
        },
        component: () => import('@/pages/create-account').then((m) => m.CreateAccount),
      },
      {
        name: 'recovery',
        path: 'recovery',
        meta: {
          pageTitle: 'Восстановления пароля',
        },
        component: () => import('@/pages/pwd-recovery').then((m) => m.PasswordRecovery),
      },
      {
        name: 'quickAccess',
        path: 'quick-access',
        alias: '',
        meta: {
          pageTitle: 'Пользователи',
          deviceIdRequired: true,
        },
        component: () => import('@/pages/quick-access').then((m) => m.QuickAccess),
        async beforeEnter(to, from, next) {
          const accountStore = useAccountsStore();
          await accountStore.update();

          nextTick(() => {
            if (!accountStore.rawList.length)
              return next({ name: 'login' });

            next();
          });
        }
      },
      {
        name: 'setCode',
        path: 'set-code',
        meta: {
          pageTitle: 'Установка кода',
          authRequired: true,
          deviceIdRequired: true,
        },
        component: () => import('@/pages/set-code').then((m) => m.SetCode),
      },
    ],
  },
  {
    path: '/cabinet',
    meta: {
      authRequired: true,
    },
    components: {
      default: CabinetLayout,
      bar: () => import('@/components/widgets/app-bar/MainAppBar.vue'),
      footer: SiteFooter,
    },
    children: [
      {
        name: 'pickRole',
        path: 'role',
        meta: {
          pageTitle: 'Выбор роли участника',
        },
        component: () => import('@/views/MemberRoleView.vue'),
      },
      {
        name: 'board',
        path: 'board',
        components: {
          default: () => import('@/components/cabinet/RoleBasedCabinetBoard.vue'),
          sidebar: RoleBasedCabinetSidebar,
        },
      },
      {
        path: 'transactions',
        name: 'transactionsList',
        meta: {
          pageTitle: 'Список операций',
        },
        component: () => import('@/pages/TransactionGrid.vue'),
      },
      {
        path: 'projects-market',
        component: () => import('@/pages/ProjectsMarket.vue'),
        meta: {
          role: 'investor',
          pageTitle: 'Рынок',
        },
      },
      {
        path: 'invest-portfolio',
        component: () => import('@/pages/InvestPortfolio.vue'),
        meta: {
          role: 'investor',
          pageTitle: 'Портфель',
        },
      },
      {
        name: 'project-direct',
        path: 'projects/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})/:human?', // project direct url,
        meta: {
          pageTitle: 'Просмотр проекта', // TODO: сюда бы вывести номер проекта
        },
        components: {
          default: () => import('@/views/ProjectView.vue'),
          sidebar: () => import('@/components/project/sidebar/RoleBasedProjectSidebar.vue'),
        },
      },
      {
        name: 'investorRegistration',
        path: 'investor-registration',
        meta: {
          pageTitle: 'Регистрация инвестора',
          role: 'investor',
          hideNavigation: true,
        },
        component: () => import('@/pages/investor-registration').then((m) => m.InvestorRegistration),
        beforeEnter(to, from, next) {
          const profileData = useProfileData();
          if (profileData.investor?.registrationState === 'done')
            return next({ name: 'board', params: { bypassBeforeEach: 'true' } });

          return next();
        },
      },
      {
        name: 'accreditedInvestor',
        path: 'accredited-investor',
        meta: {
          pageTitle: 'Статус квалиф. инвестора',
          role: 'investor',
          hideNavigation: true,
        },
        component: () => import('@/pages/accredited-investor').then((m) => m.AccreditedInvestor),
      },
      {
        name: 'loanApp',
        path: 'loan-app/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?', // direct url to loan app (new or fill)
        alias: 'loan-app/new',
        meta: {
          pageTitle: 'Заполнение заявки', // TODO: сюда бы вывести статус движения заявки?
          role: 'borrower',
          hideNavigation: true,
        },
        component: () => import('@/pages/loan-application').then((m) => m.LoanApplication),
      },
      {
        path: 'autoinvest-app',
        meta: {
          role: 'investor',
        },
        component: () => import('@/pages/autoinvest').then((m) => m.AutoinvestApplication),
        beforeEnter(to, from, next) {
          const profileData = useProfileData();
          const nextRoute = profileData.investor?.registrationState === 'done'
            ? undefined
            : { name: 'board', params: { bypassBeforeEach: 'true' } };
          return next(nextRoute);
        },
        children: [
          {
            name: 'autoinvestPickFeature',
            path: '',
            meta: {
              pageTitle: 'Новая заявка на автоинвестирование/автоследование',
              title: 'Новая заявка',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutoinvestPickFeature),
          },
          {
            name: 'autoinvestApp',
            path: 'invest/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?',
            alias: 'invest/new',
            meta: {
              pageTitle: 'Новая заявка на автоинвестирование',
              title: 'Автоинвестирование',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutofollowForm),
          },
          {
            name: 'autofollowApp',
            path: 'follow/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?',
            alias: 'follow/new',
            meta: {
              pageTitle: 'Новая заявка на автоследование',
              title: 'Автоследование',
            },
            component: () => import('@/pages/autoinvest').then((m) => m.AutofollowForm),
          },
        ],
      },
      {
        name: 'borrowerRegistration',
        path: 'borrower-registration',
        meta: {
          pageTitle: 'Регистрация заемщика',
          role: 'borrower',
          hideNavigation: true,
        },
        component: () => import('@/pages/borrower-registration').then((m) => m.BorrowerRegistration),
        beforeEnter(to, from, next) {
          const profileData = useProfileData();
          if (profileData.borrower?.registrationState === 'done')
            return next({ name: 'board', params: { bypassBeforeEach: 'true' } });

          return next();
        },
      },
      {
        name: 'profile',
        path: 'profile',
        meta: {
          pageTitle: 'Профиль',
        },
        component: () => import('@/views/ProfileView.vue'),
      },
      {
        path: 'ticket/:ticketNum',
        alias: 't/:ticketNum',
        beforeEnter(to, from, next) {
          if (to.params.ticketNum) {
            const sidePanelStore = useSidepanelStore();
            sidePanelStore.spawn({
              name: 'Tickets',
              props: {
                ticketNum: to.params.ticketNum,
              },
            });
          }

          return next({ name: 'board' });
        },
      },
    ],
  },
  {
    // таск отправляет свой ид, перенаправляем на страницу проекта инвестора
    path: '/resolve-project/:id',
    async beforeEnter(to, from, next) {
      const { response } = await Vue.http.post('/admin/ext/task/resolve_project_by_id', {
        id: to.params.id,
      });

      const project_id = response?.project_id;
      if (!project_id) {
        Vue.notify({
          type: 'error',
          text: 'Проект не найден',
          duration: -1,
        });
        return;
      }

      return next({ name: 'project-direct', params: { num: response.project_id } });
    },
  },
  /* {
    name: 'getTheApp',
    path: '/get-the-app',
    component: (() => import('@/pages/GetMobileApp.vue')),
  }, */
  {
    path: '*',
    redirect: '/account/quick-access',
  },
];

export { routes as default, routes };
